import { useCallback, useState } from 'react';
import { FormProvider, useForm } from 'react-hook-form';

import SimpleModal from 'lib/modal/simple-modal';
import {
  SimpleModalContentStyled,
  SimpleModalVerticalActionsStyled,
} from 'lib/modal/simple-modal.styles';
import { TextInputAreaStyled } from 'approot/shared/styles/inputs.style';
import { Box } from 'approot/shared/design-system/box/box.style';
import { PrimaryButton } from 'approot/shared/styles/buttons';
import { useLoggingMutation } from 'lib/apollo/apollo-hooks';
import { CREATE_FEEDBACK_SURVEY } from 'approot/shared/feedback-survey/feedback-survey.graphql';
import type {
  CreateFeedbackSurveyMutation,
  CreateFeedbackSurveyMutationVariables,
} from './feedback-survey.graphql.generated';
import ValidationMessage from 'approot/shared/validation/validation-message';
import { useSnackbar } from 'approot/shared/snackbar-notification/snackbar-notification.hooks';
import { FIELD_NAME_FEEDBACK } from 'global/constants';
import { Heading18Responsive } from 'approot/shared/styles/typography.style';
import ContactSupportWithDisclaimer from 'approot/shared/contact-support/contact-support-with-disclaimer';

import { BaseFeedbackProps } from './native-feedback-survey.constants';

type FeedbackFormData = {
  [FIELD_NAME_FEEDBACK]: string;
};

const NativeFeedbackSurveyModal = ({
  feature,
  isVisible,
  closeModal,
  contentName,
  contentType,
  contentId,
  classId,
  rating,
  setHasSubmittedFeedback,
  lessonLink,
}: BaseFeedbackProps & {
  isVisible: boolean;
  closeModal: () => void;
  rating: number;
  setHasSubmittedFeedback: (hasSubmittedFeedback: boolean) => void;
  lessonLink: string;
}) => {
  const { showError } = useSnackbar();
  const [isSubmitting, setIsSubmitting] = useState(false);

  const formMethods = useForm<FeedbackFormData>({
    defaultValues: {
      [FIELD_NAME_FEEDBACK]: '',
    },
  });
  const {
    handleSubmit,
    register,
    formState: { errors },
    reset,
  } = formMethods;

  const [createFeedbackSurvey, { loading }] = useLoggingMutation<
    CreateFeedbackSurveyMutation,
    CreateFeedbackSurveyMutationVariables
  >(CREATE_FEEDBACK_SURVEY, {
    onCompleted: () => {
      reset();
      setHasSubmittedFeedback(true);
      closeModal();
    },
    onError: () => {
      showError('We could not submit your feedback. Please try again.');
    },
  });

  const onSubmit = useCallback(
    ({ [FIELD_NAME_FEEDBACK]: feedbackText }: FeedbackFormData) => {
      setIsSubmitting(true);
      createFeedbackSurvey({
        variables: {
          input: {
            feature,
            feedbackText,
            url: lessonLink,
            data: {
              userAgent: navigator.userAgent,
              resolution: `${window.screen.width}x${window.screen.height}`,
            },
            contentId,
            contentName,
            rating,
            classId,
            contentType,
          },
        },
      }).finally(() => {
        setIsSubmitting(false);
      });
    },
    [
      createFeedbackSurvey,
      contentId,
      classId,
      rating,
      feature,
      contentType,
      contentName,
      lessonLink,
    ]
  );

  return (
    <SimpleModal
      headerTitle="Thanks for your rating!"
      title="What influenced your rating?"
      isVisible={isVisible}
      onClose={closeModal}
      size={500}
    >
      <SimpleModalContentStyled>
        <FormProvider {...formMethods}>
          <form onSubmit={handleSubmit(onSubmit)}>
            <Box $flexDirection="column" $gap={3}>
              <Heading18Responsive>
                What influenced your rating?
              </Heading18Responsive>
              <TextInputAreaStyled
                {...register(FIELD_NAME_FEEDBACK, {
                  maxLength: 5000,
                })}
                id={FIELD_NAME_FEEDBACK}
                name={FIELD_NAME_FEEDBACK}
                rows={4}
                maxLength={5000}
              />
              <ValidationMessage errors={errors} field={FIELD_NAME_FEEDBACK} />
            </Box>

            <SimpleModalVerticalActionsStyled>
              <Box $flexDirection="column" $gap={3} $justifyContent="center">
                <PrimaryButton
                  type="submit"
                  loading={loading || isSubmitting}
                  disabled={loading || isSubmitting}
                >
                  Submit
                </PrimaryButton>
                <PrimaryButton
                  variant="outlined"
                  type="button"
                  disabled={loading || isSubmitting}
                  onClick={() => {
                    onSubmit({ [FIELD_NAME_FEEDBACK]: '' });
                  }}
                >
                  Skip
                </PrimaryButton>
              </Box>
            </SimpleModalVerticalActionsStyled>
            <Box $marginTop={12} $justifyContent="flex-start">
              <ContactSupportWithDisclaimer
                disclaimerText="We don’t reply to feedback left here, but we do read it all to make Inquisitive better for teachers. If you’d like to talk to us or need help, "
                linkText="click here to chat with us"
              />
            </Box>
          </form>
        </FormProvider>
      </SimpleModalContentStyled>
    </SimpleModal>
  );
};

export default NativeFeedbackSurveyModal;
